import { useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  type OverviewQuery,
  type Period,
  type ReportScope,
  overviewSearch,
} from "../shared/overview";

export type Tab = "invoices" | "overview" | "customers" | "settings";
export type Route = {
  tab: Tab;
  document: string | null;
  edit: boolean;
  overview?: OverviewQuery;
};
const readRoute = (): Route => {
  const p = new URLSearchParams(location.search);
  const tab = p.get("tab");
  return {
    tab:
      tab === "customers" || tab === "settings" || tab === "overview"
        ? tab
        : "invoices",
    document: p.get("document"),
    edit: p.get("edit") === "1",
    overview:
      tab === "overview"
        ? {
            period: (["month", "year", "all", "custom"].includes(
              p.get("period") || "",
            )
              ? p.get("period")
              : "month") as Period,
            from: p.get("from") || undefined,
            to: p.get("to") || undefined,
            scope: ([
              "sales",
              "received",
              "outstanding",
              "overdue",
              "undated",
            ].includes(p.get("scope") || "")
              ? p.get("scope")
              : undefined) as ReportScope | undefined,
            client: p.get("client") || undefined,
            clientName: p.get("clientName") || undefined,
          }
        : undefined,
  };
};
const href = (route: Route) => {
  const p = new URLSearchParams();
  if (route.tab !== "invoices") p.set("tab", route.tab);
  if (route.document) p.set("document", route.document);
  if (route.edit) p.set("edit", "1");
  if (route.tab === "overview" && route.overview)
    new URLSearchParams(overviewSearch(route.overview)).forEach((value, key) =>
      p.set(key, value),
    );
  return "/" + (p.size ? `?${p}` : "");
};
type Entry = { key: string; depth: number; scroll: number; from?: Route };

export function useNavigation() {
  const [route, setRoute] = useState(readRoute);
  const [motion, setMotion] = useState("none");
  const entry = useRef<Entry>(
    history.state?.ailink || { key: crypto.randomUUID(), depth: 0, scroll: 0 },
  );
  const pendingScroll = useRef<number | null>(entry.current.scroll);
  const tabScroll = useRef<Record<Tab, number>>({
    invoices: 0,
    overview: 0,
    customers: 0,
    settings: 0,
  });
  const positions = useRef(new Map<string, number>());
  const reverting = useRef(false);
  useEffect(() => {
    const previous = history.scrollRestoration;
    history.scrollRestoration = "manual";
    history.replaceState(
      { ...history.state, ailink: entry.current },
      "",
      location.href,
    );
    const remember = () => {
      positions.current.set(entry.current.key, window.scrollY);
    };
    const pop = () => {
      if (reverting.current) {
        reverting.current = false;
        return;
      }
      const next: Entry = history.state?.ailink || {
        key: crypto.randomUUID(),
        depth: 0,
        scroll: 0,
      };
      if (
        !window.dispatchEvent(
          new Event("ailink:beforeleave", { cancelable: true }),
        )
      ) {
        reverting.current = true;
        history.go(entry.current.depth - next.depth || 1);
        return;
      }
      setMotion(next.depth < entry.current.depth ? "back" : "forward");
      pendingScroll.current = positions.current.get(next.key) ?? next.scroll;
      entry.current = next;
      setRoute(readRoute());
    };
    window.addEventListener("scroll", remember, { passive: true });
    window.addEventListener("popstate", pop);
    return () => {
      history.scrollRestoration = previous;
      window.removeEventListener("scroll", remember);
      window.removeEventListener("popstate", pop);
    };
  }, []);
  const navigate = (next: Route, replace = false, direction = "forward") => {
    if (
      !window.dispatchEvent(
        new Event("ailink:beforeleave", { cancelable: true }),
      )
    )
      return;
    positions.current.set(entry.current.key, window.scrollY);
    if (!route.document) tabScroll.current[route.tab] = window.scrollY;
    history.replaceState(
      {
        ...history.state,
        ailink: { ...entry.current, scroll: window.scrollY },
      },
      "",
    );
    const scroll =
      !next.document && !(next.tab === "overview" && route.tab === "overview")
        ? tabScroll.current[next.tab]
        : 0;
    const nextEntry = {
      key: crypto.randomUUID(),
      depth: entry.current.depth + (replace ? 0 : 1),
      scroll,
      from: route,
    };
    history[replace ? "replaceState" : "pushState"](
      { ailink: nextEntry },
      "",
      href(next),
    );
    entry.current = nextEntry;
    pendingScroll.current = scroll;
    setMotion(direction);
    setRoute(next);
  };
  const back = () => {
    if (entry.current.depth > 0) history.back();
    else
      navigate({ tab: "invoices", document: null, edit: false }, true, "back");
  };
  const restore = (ready: boolean) => {
    // Called from a layout effect after the destination content exists.
    if (ready && pendingScroll.current !== null) {
      window.scrollTo(0, pendingScroll.current);
      pendingScroll.current = null;
      document
        .querySelector<HTMLElement>("main")
        ?.focus({ preventScroll: true });
    }
  };
  return {
    route,
    motion,
    navigate,
    back,
    restore,
    previousRoute: entry.current.from,
  };
}

/** Follow the iPhone keyboard without moving the whole page or intercepting gestures. */
export function useKeyboardViewport() {
  useLayoutEffect(() => {
    const viewport = window.visualViewport;
    if (!viewport) return;
    const update = () => {
      const input = document.activeElement?.matches("input,textarea,select");
      const inset = input
        ? Math.max(0, innerHeight - viewport.height - viewport.offsetTop)
        : 0;
      document.documentElement.style.setProperty(
        "--keyboard-inset",
        `${inset}px`,
      );
    };
    viewport.addEventListener("resize", update);
    viewport.addEventListener("scroll", update);
    document.addEventListener("focusout", update);
    return () => {
      viewport.removeEventListener("resize", update);
      viewport.removeEventListener("scroll", update);
      document.removeEventListener("focusout", update);
      document.documentElement.style.removeProperty("--keyboard-inset");
    };
  }, []);
}
